/* aa-product.js — Aesthetic Avenue product page behaviour (PLAN.md PG3)
 *
 * Five independent pieces, each guarded so a page missing one still runs the rest:
 *   1. <aa-product-gallery>  swipe track <-> thumbnail row
 *   2. (moved) the quantity stepper, quick add and carousel arrows live in aa-card.js
 *   3. pack selector         sets the quantity and flips the price to the pack total
 *   4. <aa-variant-picker>   real Shopify options, resolved without Dawn's <variant-selects>
 *   5. .aa-delivery          the "Wysyłka dziś / Jutro u Ciebie" estimate
 *
 * No money is formatted here. Both readings of the price are rendered by Liquid through the money
 * filter and this file only chooses which one is visible — PLN and EUR are both presented (M-004)
 * and a JS formatter would have to re-implement per-market currency rules to match.
 */

/* IDEMPOTENCE. Every section on the product page loads this file, and six identical `<script src>`
   tags execute six times — the browser caches the fetch, not the execution. So each binder marks
   what it has wired with `data-aa-bound` and skips anything already marked. Without that the
   bundle button fires six add-to-cart requests from one click. The same guard also makes the
   binders safe to re-run against a subtree after a section re-renders.

   Shared namespace. The recommendation carousel injects its cards after this file has already
   run, so the routines that wire a stepper or a quick-add button have to be callable again
   against a subtree rather than only once against `document`. */
window.AAProduct = window.AAProduct || {};

(function () {
  'use strict';

  /* ==========================================================================
     1. Gallery
     ========================================================================== */

  class AAProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.aa-gallery__track');
      this.thumbs = Array.from(this.querySelectorAll('.aa-gallery__thumb'));
      if (!this.track || this.thumbs.length === 0) return;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.select(Number(thumb.dataset.index)));
      });

      // Reflect the swipe back onto the thumbnails. rAF-throttled because scroll fires far more
      // often than the row needs repainting.
      let ticking = false;
      this.track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            this.syncFromScroll();
            ticking = false;
          });
        },
        { passive: true }
      );
    }

    select(index) {
      const slide = this.track.children[index];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      this.mark(index);
    }

    syncFromScroll() {
      const centre = this.track.scrollLeft + this.track.clientWidth / 2;
      let nearest = 0;
      let best = Infinity;
      Array.from(this.track.children).forEach((slide, i) => {
        const mid = slide.offsetLeft - this.track.offsetLeft + slide.clientWidth / 2;
        const distance = Math.abs(mid - centre);
        if (distance < best) {
          best = distance;
          nearest = i;
        }
      });
      this.mark(nearest);
    }

    mark(index) {
      this.thumbs.forEach((thumb) => {
        const on = Number(thumb.dataset.index) === index;
        thumb.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      });
    }
  }

  if (!customElements.get('aa-product-gallery')) {
    customElements.define('aa-product-gallery', AAProductGallery);
  }

  /* ==========================================================================
     3. Pack selector
     ========================================================================== */

  document.querySelectorAll('.aa-product').forEach((section) => {
    const radios = section.querySelectorAll('.aa-pack__items input[name$="-pack"]');
    if (radios.length === 0) return;
    if (section.dataset.aaPackBound === 'true') return;
    section.dataset.aaPackBound = 'true';

    const qtyRoot = section.querySelector('.aa-qty');
    const qtyInput = qtyRoot && qtyRoot.querySelector('.aa-qty__input');
    // Plural: the sticky bar renders a second .aa-price, and both readings must stay in step.
    const prices = section.querySelectorAll('.aa-price');
    const setPackMode = (on) => prices.forEach((el) => { el.dataset.packMode = on ? 'true' : 'false'; });
    const packQty = Number(section.dataset.packQty || 0);

    function apply(value) {
      const isPack = Number(value) > 1;
      if (qtyInput) {
        qtyInput.value = isPack ? String(packQty) : '1';
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      setPackMode(isPack);
    }

    radios.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.checked) apply(radio.value);
      });
    });

    // Typing a quantity by hand contradicts the pack pill; drop back to the single so the price
    // line never claims a pack total for a quantity the customer did not pick.
    if (qtyInput) {
      qtyInput.addEventListener('change', () => {
        const single = section.querySelector('.aa-pack__items input[value="1"]');
        if (!single || single.checked) return;
        if (Number(qtyInput.value) !== packQty) {
          single.checked = true;
          setPackMode(false);
        }
      });
    }
  });

  /* ==========================================================================
     4. Variant picker
     ========================================================================== */

  class AAVariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('script[data-aa-variants]');
      if (!data) return;
      try {
        this.variants = JSON.parse(data.textContent);
      } catch (error) {
        return;
      }

      this.section = this.closest('.aa-product');
      this.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"]')) this.onChange();
      });
      this.reflectAvailability();
    }

    get selection() {
      return Array.from(this.querySelectorAll('input[type="radio"]:checked')).map((input) => input.value);
    }

    match(values) {
      return this.variants.find((variant) =>
        variant.options.every((option, index) => option === values[index])
      );
    }

    /* Shopify exposes availability per variant, never per option value, so each value is tested
       against the current selection of the other options. */
    reflectAvailability() {
      const current = this.selection;
      this.querySelectorAll('input[type="radio"]').forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;
        const trial = current.slice();
        trial[position] = input.value;
        const variant = this.match(trial);
        input.disabled = !variant || variant.available === false;
        if (input.checked) input.disabled = false;
      });
    }

    onChange() {
      const variant = this.match(this.selection);
      this.reflectAvailability();
      if (!variant) return;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      const hidden = this.section && this.section.querySelector('.product-variant-id');
      if (hidden) {
        hidden.value = variant.id;
        hidden.disabled = !variant.available;
      }

      const submit = this.section && this.section.querySelector('.aa-buy__submit');
      if (submit) submit.disabled = !variant.available;

      this.rerenderPrice(variant.id);
      this.refreshDependents(variant.id);
    }

    /* The wholesale table and the bundle both quote per-unit money derived from the variant price,
       so they go stale the moment the variant changes. Any section that marks itself
       `data-aa-variant-refresh` is re-rendered server-side, which keeps the money filter in play. */
    refreshDependents(variantId) {
      document.querySelectorAll('[data-aa-variant-refresh]').forEach((node) => {
        const id = node.dataset.sectionId;
        if (!id || !node.id) return;
        fetch(`${this.dataset.url}?variant=${variantId}&section_id=${id}`)
          .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
          .then((html) => {
            const next = new DOMParser().parseFromString(html, 'text/html').getElementById(node.id);
            if (next) node.replaceWith(next);
          })
          .catch(() => {
            /* Stale-but-present beats blank. */
          });
      });
    }

    /* The price line is re-rendered by the server rather than assembled here, so the pack total,
       the compare-at, the discount percentage and the VAT figure all stay Liquid's job. */
    rerenderPrice(variantId) {
      const priceBlocks = this.section ? [...this.section.querySelectorAll('.aa-price')] : [];
      const targets = priceBlocks.filter((el) => el.id);
      if (targets.length === 0) return;
      const endpoint = `${this.dataset.url}?variant=${variantId}&section_id=${this.dataset.section}`;

      fetch(endpoint)
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          targets.forEach((current) => {
            const next = parsed.getElementById(current.id);
            if (!next) return;
            // The freshly rendered block always starts in unit mode; carry the current pill over.
            next.dataset.packMode = current.dataset.packMode || 'false';
            current.replaceWith(next);
          });
        })
        .catch(() => {
          /* Leave the previous price standing rather than blanking it on a failed fetch. */
        });
    }
  }

  if (!customElements.get('aa-variant-picker')) {
    customElements.define('aa-variant-picker', AAVariantPicker);
  }

  /* ==========================================================================
     5. Delivery estimate
     ========================================================================== */

  const WAREHOUSE_TZ = 'Europe/Warsaw';

  /* Reads the wall clock in Poland. The visitor's own clock is the wrong one — the cut-off is a
     warehouse time (D6) — and Liquid's `now` is UTC and would be frozen into the page cache. */
  function warehouseNow() {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: WAREHOUSE_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());

    const get = (type) => Number(parts.find((part) => part.type === type).value);
    return {
      date: new Date(Date.UTC(get('year'), get('month') - 1, get('day'))),
      minutes: get('hour') * 60 + get('minute'),
    };
  }

  function isWorkingDay(date) {
    const day = date.getUTCDay();
    return day >= 1 && day <= 5;
  }

  function addWorkingDays(date, count) {
    const result = new Date(date.getTime());
    let left = count;
    while (left > 0) {
      result.setUTCDate(result.getUTCDate() + 1);
      if (isWorkingDay(result)) left -= 1;
    }
    return result;
  }

  function daysBetween(from, to) {
    return Math.round((to.getTime() - from.getTime()) / 86400000);
  }

  document.querySelectorAll('[data-aa-delivery]').forEach((root) => {
    const script = root.querySelector('script[data-aa-delivery-i18n]');
    if (!script) return;
    if (root.dataset.aaBound === 'true') return;
    root.dataset.aaBound = 'true';

    let text;
    try {
      text = JSON.parse(script.textContent);
    } catch (error) {
      return;
    }

    const [cutoffHour, cutoffMinute] = String(root.dataset.cutoff || '14:00').split(':').map(Number);
    const cutoff = (cutoffHour || 0) * 60 + (cutoffMinute || 0);
    const transit = Math.max(1, Number(root.dataset.transit || 1));

    const now = warehouseNow();
    const today = now.date;

    let ship = new Date(today.getTime());
    if (!isWorkingDay(ship) || now.minutes >= cutoff) {
      ship = addWorkingDays(ship, 1);
    }
    const arrive = addWorkingDays(ship, transit);

    // Monday-first, matching how the weekday lists are authored in the locale files.
    const weekdayIndex = (date) => (date.getUTCDay() + 6) % 7;
    const list = (value) => String(value || '').split(',');
    const names = list(text.weekdays);
    const namesOn = list(text.weekdays_on);
    // The design stamps the date as "7.09" — day unpadded, month padded to two digits.
    const stamp = (date) => `${date.getUTCDate()}.${String(date.getUTCMonth() + 1).padStart(2, '0')}`;

    const shipOffset = daysBetween(today, ship);
    let shipLine = text.today;
    if (shipOffset === 1) {
      shipLine = text.tomorrow;
    } else if (shipOffset > 1) {
      shipLine = String(text.on_day).replace('%DAY%', namesOn[weekdayIndex(ship)] || '');
    }

    const shipEl = root.querySelector('[data-aa-delivery-ship]');
    if (shipEl) shipEl.textContent = shipLine;

    const arriveEl = root.querySelector('[data-aa-delivery-arrive]');
    if (arriveEl && root.dataset.showDate === 'true') {
      const arriveOffset = daysBetween(today, arrive);
      const line =
        arriveOffset === 1
          ? String(text.arrives_tomorrow).replace('%DATE%', stamp(arrive))
          : String(text.arrives_on)
              .replace('%DAY%', names[weekdayIndex(arrive)] || '')
              .replace('%DATE%', stamp(arrive));
      arriveEl.textContent = line;
      arriveEl.hidden = false;
    }
  });
})();

/* ==========================================================================
   6. Trust band — keep the free-shipping figures honest after a cart change
   ==========================================================================
   The bar is rendered by Liquid so every amount goes through the money filter. When the cart
   changes the section is re-fetched from the server rather than recomputed here: the alternative
   is a JS money formatter that would have to match Shopify's per-market currency rules exactly,
   and would drift the moment a market's format differs.

   Dawn publishes cart changes on the `cart-update` topic through pubsub.js, which
   layout/theme.liquid already loads. */
(function () {
  'use strict';

  const bands = document.querySelectorAll('[data-aa-assurances]');
  if (bands.length === 0) return;
  if (typeof window.subscribe !== 'function') return;
  // One subscription, not one per script execution, or a single cart change fires six re-fetches.
  if (window.AAProduct.cartWatch) return;
  window.AAProduct.cartWatch = true;

  function refresh(band) {
    const id = band.dataset.sectionId;
    if (!id) return;
    fetch(`${window.location.pathname}?section_id=${id}`)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((html) => {
        const next = new DOMParser().parseFromString(html, 'text/html').getElementById(band.id);
        if (next) band.replaceWith(next);
      })
      .catch(() => {
        /* Stale figures beat a blank row. */
      });
  }

  window.subscribe('cart-update', () => {
    document.querySelectorAll('[data-aa-assurances]').forEach(refresh);
  });
})();

/* ==========================================================================
   7. Tabs
   ========================================================================== */
(function () {
  'use strict';

  document.querySelectorAll('[data-aa-tabs]').forEach((group) => {
    const tabs = Array.from(group.querySelectorAll('.aa-tabs__tab'));
    const panels = Array.from(group.querySelectorAll('.aa-tabs__panel'));
    if (tabs.length === 0) return;
    if (group.dataset.aaBound === 'true') return;
    group.dataset.aaBound = 'true';

    function show(index) {
      tabs.forEach((tab, i) => tab.setAttribute('aria-selected', i === index ? 'true' : 'false'));
      panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => show(index));
      tab.addEventListener('keydown', (event) => {
        const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (step === 0) return;
        event.preventDefault();
        const next = (index + step + tabs.length) % tabs.length;
        show(next);
        tabs[next].focus();
      });
    });

    // Panels are rendered `hidden` so the description is in the HTML for crawlers without the
    // page flashing every tab at once before this runs.
    show(0);
  });
})();

/* ==========================================================================
   8. Wholesale table — highlight the tier the current quantity actually buys
   ========================================================================== */
(function () {
  'use strict';

  const tables = document.querySelectorAll('[data-aa-tiers]');
  if (tables.length === 0) return;

  const qty = document.querySelector('.aa-product .aa-qty__input');
  if (!qty) return;
  if (qty.dataset.aaTiersBound === 'true') return;
  qty.dataset.aaTiersBound = 'true';

  function mark() {
    const wanted = Number(qty.value) || 1;
    tables.forEach((table) => {
      let winner = null;
      table.querySelectorAll('tbody tr').forEach((row) => {
        row.removeAttribute('data-active');
        if (Number(row.dataset.min) <= wanted) winner = row;
      });
      if (winner) winner.setAttribute('data-active', '');
    });
  }

  qty.addEventListener('change', mark);
  qty.addEventListener('input', mark);
  mark();
})();

/* ==========================================================================
   9. Cart writes — the bundle CTA
   ==========================================================================
   The bundle posts through the shared `AACard.addToCart` (aa-card.js), which publishes Dawn's
   `cart-update` and opens the drawer, so the bundle and the card's quick add behave identically. */
(function () {
  'use strict';

  document.querySelectorAll('[data-aa-bundle-add]').forEach((button) => {
    const panel = button.closest('aa-bundle');
    const data = panel && panel.querySelector('script[data-aa-bundle-items]');
    if (!data) return;
    if (button.dataset.aaBound === 'true') return;
    button.dataset.aaBound = 'true';
    button.addEventListener('click', () => {
      let items;
      try {
        items = JSON.parse(data.textContent);
      } catch (error) {
        return;
      }
      if (items.length > 0 && window.AACard && window.AACard.addToCart) {
        window.AACard.addToCart(items, button);
      }
    });
  });

})();

/* ==========================================================================
   10. Recommendations
   ==========================================================================
   Shopify computes related products asynchronously, so they are not available to Liquid on first
   paint. The section renders an empty shell and fills it from the Recommendations API — the same
   approach Dawn's own related-products section takes. */
(function () {
  'use strict';

  const shells = document.querySelectorAll('aa-recommendations');
  if (shells.length === 0) return;

  shells.forEach((shell) => {
    const url = shell.dataset.url;
    if (!url || shell.children.length > 0) return;
    if (shell.dataset.aaBound === 'true') return;
    shell.dataset.aaBound = 'true';

    fetch(url)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((html) => {
        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('aa-recommendations');
        if (!next || next.children.length === 0) return;
        shell.innerHTML = next.innerHTML;
        // These cards did not exist when the steppers and quick-adds were wired.
        const card = window.AACard || {};
        if (card.wireSteppers) card.wireSteppers(shell);
        if (card.wireQuickAdd) card.wireQuickAdd(shell);
        if (card.wireCarousels) card.wireCarousels(shell);
      })
      .catch(() => {
        /* An empty section is the correct outcome for a product with no recommendations. */
      });
  });
})();

/* ==========================================================================
   11. Sticky add to cart
   ==========================================================================
   Shown once the real buy row has scrolled out of view ABOVE the viewport, hidden again when it
   comes back — and hidden while the buy row is still below the fold, because a shopper who has not
   reached the page's own CTA does not need a second one covering the content.

   A scroll listener, NOT an IntersectionObserver, and that is deliberate: an observer only fires
   when a threshold is crossed, so a jump straight to the bottom of the page — a restored scroll
   position, an anchor link, a tap on the scrollbar — takes the buy row from below the fold to above
   the viewport without it ever intersecting, no callback fires, and the bar never appears. Reading
   the rect once per animation frame is cheap and has no such blind spot.

   The bar carries no form of its own — its button is `form="<the buy form>"`, so quantity, variant
   and pack selection all come from the real form. What it does need is its own busy state: Dawn's
   product-form.js puts `.loading` on the button INSIDE <product-form>, which by definition is the
   one the shopper cannot see when the sticky bar is up. Without mirroring it, tapping the sticky
   button looks like nothing happened (the fault fixed for the other three buttons in
   log:2026-09-08-pack-gap-and-cart-spinners). */
(function () {
  'use strict';

  document.querySelectorAll('[data-aa-sticky]').forEach((bar) => {
    if (bar.dataset.aaBound === 'true') return;
    bar.dataset.aaBound = 'true';

    // The buy row is unique on the page, so look it up directly rather than by walking siblings,
    // which would break the moment anything is rendered between the section and the bar.
    const buyRow = document.querySelector('.aa-buy');
    if (!buyRow) return;

    bar.hidden = false;
    bar.dataset.visible = 'false';

    let queued = false;
    const update = () => {
      queued = false;
      // Above the viewport, not merely off screen: below the fold means "not reached yet".
      const past = buyRow.getBoundingClientRect().bottom < 0;
      const next = past ? 'true' : 'false';
      if (bar.dataset.visible !== next) bar.dataset.visible = next;
    };
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    // Late-loading images and the recommendations row both change the buy row's position.
    window.addEventListener('load', schedule);
    update();

    // Mirror the real button's busy state onto ours.
    const realSubmit = buyRow.querySelector('[type="submit"]');
    const stickySubmit = bar.querySelector('[data-aa-sticky-submit]');
    if (realSubmit && stickySubmit) {
      const sync = () => {
        const busy = realSubmit.classList.contains('loading');
        stickySubmit.setAttribute('aria-busy', busy ? 'true' : 'false');
      };
      new MutationObserver(sync).observe(realSubmit, {
        attributes: true,
        attributeFilter: ['class'],
      });
      sync();
    }
  });
})();

/* ==========================================================================
   12. Desktop buy card — keep its two halves stuck together
   ==========================================================================
   The frame draws the right column as ONE card: the buy column on top, the trust rows below it,
   inside a single rounded border. In the theme those are two different SECTIONS, siblings in the
   template, so the card is assembled from two adjacent grid items sharing a border.

   That works for the static layout, but not for `position: sticky`: two siblings given the same
   `top` would pin to the same line and overlap. The trust band therefore sticks at the buy
   column's height, published here as `--aa-buy-offset`, so the pair travels as one card.

   Measured rather than assumed, because the buy column's height depends on what the product has —
   the pack selector, the multipack notice and the similar-products row all come and go. */
(function () {
  'use strict';

  const info = document.querySelector('.aa-product__info');
  const band = document.querySelector('.aa-section--assurances');
  if (!info || !band || typeof ResizeObserver === 'undefined') return;

  const grid = info.closest('#MainContent');

  const publish = () => {
    const twoColumn = window.getComputedStyle(info).gridColumnStart === '8';
    if (grid) grid.classList.toggle('aa-card-joined', twoColumn);

    band.style.setProperty('--aa-buy-offset', twoColumn ? `${Math.round(info.offsetHeight)}px` : '0px');

    // The two halves stick inside the same grid area but have different heights, so each stops
    // when ITS OWN bottom reaches the area's end — the band, sitting lower, gets there one band
    // height earlier, stops, and the buy column carries on sliding underneath it. Giving the buy
    // column a bottom margin of exactly the band's height makes both reach the limit together, so
    // the card releases as one piece instead of folding into itself.
    info.style.setProperty('--aa-band-h', twoColumn ? `${Math.round(band.offsetHeight)}px` : '0px');
  };

  const ro = new ResizeObserver(publish);
  ro.observe(info);
  ro.observe(band);
  window.addEventListener('resize', publish, { passive: true });
  publish();
})();
